"use client";

import { useState, type KeyboardEvent } from "react";
import { ChevronDown } from "lucide-react";
import { H3 } from "@/shared/components";
import { displayFontClassName } from "@/lib/fonts";
import { cn } from "@/lib/utils";
import type { TableTheme } from "./tableTheme";

interface TableBlockTableSectionProps {
  rows: string[][];
  title?: string | null;
  theme: TableTheme;
  borderClass: string;
  columnMinWidths?: number[];
  isLast?: boolean;
  accordionMode?: boolean;
  isOpen?: boolean;
  onTitleClick?: () => void;
}

export function TableBlockTableSection({
  rows,
  title,
  theme,
  borderClass,
  columnMinWidths = [],
  isLast = false,
  accordionMode = false,
  isOpen = true,
  onTitleClick,
}: TableBlockTableSectionProps) {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  if (!rows || rows.length === 0) {
    return null;
  }

  const [headerRow, ...bodyRows] = rows;
  const trimmedTitle = title?.trim();
  const showContent = !accordionMode || !trimmedTitle || isOpen;
  const titleClassName = cn(
    "text-heading-3-mobile font-bold leading-[1.2] md:text-heading-3 md:font-semibold md:leading-[1.2]",
    theme.tableTitle
  );

  const handleRowClick = (rowIndex: number) => {
    setSelectedRowIndex((current) => (current === rowIndex ? null : rowIndex));
  };

  const getInteractiveRowClass = (rowIndex: number) =>
    cn(
      "cursor-pointer border-b transition-colors",
      (accordionMode || !isLast) && "last:border-b-0",
      borderClass,
      theme.rowInteractive,
      selectedRowIndex === rowIndex && theme.rowSelected,
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    );

  const handleRowKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    rowIndex: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRowClick(rowIndex);
    }
  };

  return (
    <section
      className={cn(
        "w-full min-w-0",
        accordionMode
          ? cn("border-b last:border-b-0", borderClass)
          : !isLast && cn("border-b", borderClass)
      )}
    >
      {trimmedTitle && (
        <div
          className={cn(
            "px-4 py-4 md:px-6 md:py-6 desktop:px-10",
            theme.tableTitleRow,
            !accordionMode && cn("border-b", borderClass),
            accordionMode &&
              isOpen &&
              showContent &&
              cn("border-b", borderClass)
          )}
        >
          {accordionMode && onTitleClick ? (
            <button
              type="button"
              onClick={onTitleClick}
              className={cn(
                "flex w-full items-center justify-between gap-4 text-left",
                displayFontClassName,
                titleClassName
              )}
              aria-expanded={isOpen}
            >
              <span className="min-w-0 flex-1">{trimmedTitle}</span>
              <ChevronDown
                className={cn(
                  "size-10 shrink-0 transition-transform duration-200",
                  theme.tableTitle,
                  isOpen && "rotate-180"
                )}
                strokeWidth={1.5}
                aria-hidden
              />
            </button>
          ) : (
            <H3 className={titleClassName}>{trimmedTitle}</H3>
          )}
        </div>
      )}

      {showContent && (
        <div className="overflow-x-auto">
          <table className="w-max min-w-full border-collapse text-base">
            {columnMinWidths.length > 0 && headerRow && (
              <colgroup>
                {headerRow.map((_, cellIndex) => (
                  <col
                    key={cellIndex}
                    style={{
                      minWidth: columnMinWidths[cellIndex]
                        ? `${columnMinWidths[cellIndex]}px`
                        : undefined,
                    }}
                  />
                ))}
              </colgroup>
            )}
            {headerRow && headerRow.length > 0 && (
              <thead>
                <tr className={cn("border-b", borderClass)}>
                  {headerRow.map((cell, cellIndex) => (
                    <th
                      key={cellIndex}
                      scope="col"
                      className={cn(
                        "px-4 py-4 text-left font-normal leading-[1.2] md:px-6 desktop:px-10 desktop:py-4",
                        cellIndex > 0 && "whitespace-nowrap",
                        theme.headerCell
                      )}
                      style={
                        columnMinWidths[cellIndex]
                          ? { minWidth: `${columnMinWidths[cellIndex]}px` }
                          : undefined
                      }
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {bodyRows.map((row, rowIndex) => {
                const isSelected = selectedRowIndex === rowIndex;

                return (
                  <tr
                    key={rowIndex}
                    className={getInteractiveRowClass(rowIndex)}
                    onClick={() => handleRowClick(rowIndex)}
                    onKeyDown={(event) => handleRowKeyDown(event, rowIndex)}
                    tabIndex={0}
                    aria-selected={isSelected}
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={cn(
                          "px-4 py-4 align-top leading-6 md:px-6 md:py-6 desktop:px-10",
                          cellIndex > 0 && "whitespace-nowrap",
                          theme.bodyCell
                        )}
                        style={
                          columnMinWidths[cellIndex]
                            ? { minWidth: `${columnMinWidths[cellIndex]}px` }
                            : undefined
                        }
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
